import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeSlash, SpinnerGap, ArrowSquareOut } from '@phosphor-icons/react';
import { detectSubdomainArea } from '@/utils/detectSubdomain';
import SiteFooter from '@/components/SiteFooter';
import ForgotPasswordModal from '@/components/auth/ForgotPasswordModal';
import SocialAuthButtons, { SocialAuthDivider } from '@/components/auth/SocialAuthButtons';
import { captureReferFromUrl } from '@/shared/auth/referCookie';
import { getPendingCart, claimPendingCart, getPostAuthTarget, clearPostAuthTarget } from '@/shared/auth/pendingCart';
import { myApi } from '@/my/api';

const LAST_AREA_KEY = 'ihtp_last_area';
const AREA_HREF = { my: '/my', media: '/media', crm: '/' };

/**
 * Returns the value of the `next` query param if it is a safe relative path
 * (starts with `/`, no protocol/host). Anything else returns null so we never
 * open-redirect to an external attacker URL.
 */
function safeNextParam(raw) {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null; // protocol-relative
  return raw;
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = safeNextParam(searchParams.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  // If a CRM session is already valid, skip the login form entirely.
  useEffect(() => {
    // Captura REFER do URL na primeira visita à página de login — assim
    // o cookie fica persistido mesmo que o utilizador clique em "criar
    // conta" só passados alguns minutos.
    captureReferFromUrl();
    if (user) {
      // Pending cart claim — se o WP enviou items mas o utilizador já tinha
      // sessão activa quando chegou ao /login (rara), faz claim e segue.
      if (getPendingCart()) {
        claimPendingCart(myApi).finally(() => {
          const target = getPostAuthTarget() || "/my/carrinho";
          clearPostAuthTarget();
          window.location.href = target;
        });
        return;
      }
      // Honor `?next=` even when there's already an active session — common
      // when WordPress sends a logged-in user back to MYIHTP cart.
      if (nextParam) window.location.href = nextParam;
      else navigate('/areas', { replace: true });
    }
  }, [user, navigate, nextParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { areas } = await login(email, password);

      // Pending cart (WP intent flow): se houver items pendentes, faz claim
      // ao carrinho do utilizador autenticado e redirecciona para
      // /my/carrinho — sobrepõe-se ao `nextParam` e às heurísticas de área
      // porque é o objectivo principal da sessão.
      if (getPendingCart()) {
        try { await claimPendingCart(myApi); } catch (cE) {
          if (process.env.NODE_ENV !== "production") console.warn("login claim failed:", cE);
        }
        const target = getPostAuthTarget() || "/my/carrinho";
        clearPostAuthTarget();
        window.location.href = target;
        return;
      }

      // If the caller asked us to come back to a specific page (e.g. WP cart
      // redirect), honour it before any of the area heuristics kick in.
      if (nextParam) {
        window.location.href = nextParam;
        return;
      }
      // Priority: (1) subdomain hint (crm.* → crm) (2) `/my` por defeito
      //   se o utilizador tiver acesso — é a área principal do cliente,
      //   o CRM é para uso admin e acede-se via crm.* ou pelo botão "CRM"
      //   no header MYIHTP. (3) single-area fast path (4) selector.
      const subdomain = detectSubdomainArea();
      let target = null;
      if (subdomain && areas.includes(subdomain)) target = subdomain;
      else if (areas.includes('my')) target = 'my';
      else if (areas.length === 1) target = areas[0];

      if (target && AREA_HREF[target]) {
        try { window.localStorage.setItem(LAST_AREA_KEY, target); } catch (e) {
          if (process.env.NODE_ENV !== "production") console.warn("LoginPage: localStorage write failed", e);
        }
        window.location.href = AREA_HREF[target];
      } else {
        navigate('/areas');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erro ao iniciar sessão');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex-1 flex">
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden bg-[#0F1829]">
        <div className="absolute top-12 left-12 z-10">
          <img src="/logo-ihtp.svg" alt="I Have the Power" className="h-7 w-auto" data-testid="login-brand-logo" />
        </div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-black tracking-tighter mb-3" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Posso ainda não ter chegado onde queria, mas estou mais perto que ontem.
          </h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-[400px]">
          <div className="flex justify-end mb-4">
            <a href="https://www.ihavethepower.net" data-testid="login-go-to-site"
              className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1.5 underline underline-offset-2 transition-colors">
              Ir para site
              <ArrowSquareOut size={14} />
            </a>
          </div>
          <div className="mb-10">
            <img src="/logo-ihtp-dark.svg" alt="I Have the Power" className="h-7 w-auto mb-4" data-testid="login-brand-logo-mobile" />
            <p className="text-[#6B7280] text-sm">Bem-vindo de volta. Entre na sua conta.</p>
          </div>

          {error && (
            <div data-testid="login-error" className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-[#FF2B2B] text-sm">
              {typeof error === 'string' ? error : error.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1.5 block">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0F1829] transition-all" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1.5 block">Palavra-Passe</label>
              <div className="relative">
                <input data-testid="login-password" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-3 py-2.5 pr-10 border border-[#E5E7EB] rounded-md text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0F1829] transition-all" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]">
                  {showPw ? <Eye size={18} /> : <EyeSlash size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full py-2.5 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <SpinnerGap size={18} className="animate-spin" /> : null}
              {loading ? 'A entrar...' : 'Entrar'}
            </button>
            <SocialAuthDivider />
            <SocialAuthButtons />
            <div className="text-center pt-1 space-y-2">
              <button type="button" onClick={() => setForgotOpen(true)} data-testid="login-forgot-password-btn"
                className="text-xs text-[#6B7280] hover:text-[#0F1829] underline underline-offset-2 transition-colors">
                Esqueci-me da palavra-passe
              </button>
              <div className="text-xs text-[#6B7280]">
                Não tem conta? {' '}
                <Link to="/register" data-testid="login-create-account"
                  className="text-[#0F1829] font-medium hover:underline underline-offset-2">
                  Criar conta
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
      </div>

      <SiteFooter />
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} initialEmail={email} />
    </div>
  );
}
